/* Sundays in Bed — Klang Valley construction-cost BOQ engine.
   Faithful JS port of Sundays_in_Bed_Klang_Valley_Cost_Model_V9_Final.xlsx (MODEL_CONFIG
   sheet; every other sheet in that workbook is a formula-driven view of MODEL_CONFIG, or
   narrative documentation — never a numeric source on its own).
   The workbook is the costing authority: this file only interprets its rules/rates — it
   must not invent quantities, rates, tiers, or factors beyond what SIBBoqData carries.
   This engine prices CONSTRUCTION / RENOVATION works only (the "contractor works" estimate).
   The Sundays in Bed design/styling SERVICE FEE (sib-estimator.js) is a separate, untouched model.

   v9 changes from the v7 engine this replaces — all still driven by the SAME questionnaire
   inputs (site condition, project purpose, home type, built-up sqft, room counts, finish
   tier, work checkboxes, layout change): no new UI question, per the workbook's own note
   ("01_Project: Built-up area and active room rows determine the live zone and bedroom
   count. No extra UI question is required.").

   1. Low/Expected/High are now genuinely three different SCOPES, not one scope at three
      prices. Every rule carries a scopeClass (Core/Typical/Optional):
        - Core: full quantity in every band.
        - Typical: PROJECT_DEFAULTS.lowTypicalScopeWeight (0.75) quantity in Low; full in
          Expected/High.
        - Optional: 0 in Low/Expected always (this light UI has no per-item Optional picker,
          so nothing is ever "explicitly selected" the way a fuller UI's scope controls
          would allow — matches v7's behaviour for Low/Expected exactly). In High, an
          Optional rule only appears if RULES[].v9HighEligible is true, at a quantity
          weighted by the project's whole-home size zone (SIZE_ZONES[].highEligibleWeight —
          0 at Zone 1 up to 1.0 at Zone 4). This is why v9 KEEPS Optional rules at data-
          generation time where v7 dropped them outright — High needs them.
      "High is a true superset of Expected" is an explicit acceptance-test invariant (12_
      Acceptance_Tests R53): every band computation below runs Core/Typical unconditionally
      into all three bands it applies to, so this holds by construction, not by a patch.
   2. Whole-home size-zone engine (RULES[].v9ZoneClass): 'Private built-in' rules (e.g. each
      bedroom's own wardrobe) get a modest quantity uplift once total built-up area passes
      ~2,000 sqft; 'Shared cabinetry' rules (household-linked storage) get a larger uplift.
      This is a REAL quantity increase (more linear ft of storage), not a final-price
      multiplier — it applies identically across Low/Expected/High for whichever rules it
      touches, stacked on top of (1) above, not instead of it.
   3. Bedroom-aware shared storage (RULES[].v9BedroomLinked): on top of (2), 'Shared
      cabinetry' rules specifically flagged bedroom-linked get an ADDITIONAL quantity factor
      once active bedroom count reaches 4+ (BEDROOM_SHARED_FACTORS) — household-linked
      storage genuinely needs more with a bigger household. Each bedroom's own wardrobe is
      'Private built-in', never bedroom-linked, so this never double-counts per-bedroom
      storage — confirmed against the workbook's own worked examples (12_Acceptance_Tests
      R49/R50).
   4. Large-format loose FF&E (RATES[item].largeRoomScale) gets an additional quantity factor
      from the ROOM's own area (not the whole-home size zone) via LARGE_ROOM_SCALE, per tier.
      Present in the workbook since at least v7 (3C panel) but never wired up until now — a
      real gap in what v7 shipped, not a new v9 concept; noted for the record.
   5. Signature-tier pricing for "brand mix" FF&E items (RATES[item].ffeBrandMix, e.g. loose
      sofas/beds/tables) is computed, not looked up: RATES[item].signatureRate is
      intentionally null for these — Signature price blends the Premium rate with a separate
      luxury/imported reference rate (RATES[item].luxuryRate) at band-specific shares
      (SIGNATURE_BRAND_MIX: 80/20 Low, 50/50 Expected, 30/70 High, Premium-vs-luxury).
   6. Premium-tier Low-band pricing for the SAME brand-mix FF&E items blends 50% Essential +
      50% Premium unit pricing instead of Premium-rate times a low factor (12_Acceptance_
      Tests R47 gives the exact worked check this implements). Every other tier/band on these
      items, and every non-brand-mix item at every tier/band, still uses a plain rate x
      low/high factor — tier-specific where the rate card provides one (RATES[item].
      essential/premium/signatureLow|HighFactor), falling back to the item's single generic
      lowFactor/highFactor otherwise (only FF&E rows carry tier-specific factors in this
      workbook; every other trade behaves exactly as in v7).
   7. Contingency is still never added to the total (v9 keeps v7's policy exactly); rental
      factors, the Electrical/Plumbing "Point" runtime scope-depth logic, wet-tiling
      separation, and the bathroom-scope factor table are all unchanged from v7 — see the
      generator's own comments for what did and did not change underneath them. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(require('./sib-boq-data.js')); }
  else { root.SIBBoqEngine = factory(root.SIBBoqData); }
}(typeof self !== 'undefined' ? self : this, function (Data) {
  'use strict';

  var TIER_NAMES = Data.TIER_NAMES; /* ['Essential','Premium','Signature'] */
  var BANDS = ['low', 'expected', 'high'];

  /* ---- UI room key -> model room type / subtype / defaults ----
     "Keep it light" flow: the questionnaire only asks room counts (+/- circles), so every
     instance gets the model's own documented default subtype/area/driver (02_Rooms example
     rows) rather than asking per-room follow-ups. Hacking/Waterproofing/HVAC are not exposed
     as checkboxes anywhere in the UI spec, so each room type carries its own documented
     default for those three (also lifted from the 02_Rooms example rows); every other work
     group follows the visitor's global work-scope checkboxes — including "Wet tiling" (v9
     keeps v7's separation of wet-area tiling from dry flooring; see WORK_GROUP_FLAG below). */
  var ROOM_DEFAULTS = {
    bed:    { roomType: 'Bedroom',      subtype: 'Primary bedroom',            area: 300, driver: 2, perimeter: null, hacking: 'N', waterproof: 'N', hvac: 'Y' },
    liv:    { roomType: 'Living room',  subtype: 'Main living',                area: 300, driver: 0, perimeter: null, hacking: 'N', waterproof: 'N', hvac: 'Y' },
    din:    { roomType: 'Dining room',  subtype: 'Formal dining',              area: 160, driver: 6, perimeter: null, hacking: 'N', waterproof: 'N', hvac: 'N' },
    kit:    { roomType: 'Kitchen',      subtype: 'Wet + Dry',                  area: 100, driver: 0, perimeter: null, hacking: 'Y', waterproof: 'N', hvac: 'N' },
    /* Wet kitchen deliberately runs on the Utility / yard rate rules, not Kitchen's. Two
       reasons. Rules match on roomType, and the V9 workbook (which this data is generated
       from, and which must not be hand-edited) has no 'Wet kitchen' type — an invented one
       would match zero rules and silently cost RM0. And Kitchen's fixture rules are baseQty 1
       PER ROOM INSTANCE, so putting both halves on Kitchen would bill two hoods, two appliance
       sets and two islands. The yard rules give it what the space actually needs: waterproofing,
       wet floor tile, hot/cold water, drain, a utility sink and the washer/dryer. The cooking
       fixtures (hood, hob, countertop, cabinets) stay counted exactly once, on the dry pick. */
    wetkit: { roomType: 'Utility / yard', subtype: 'Wet kitchen',              area: 70,  driver: 0, perimeter: null, hacking: 'N', waterproof: 'Y', hvac: 'N' },
    bath:   { roomType: 'Bathroom',     subtype: 'Ensuite',                    area: 50,  driver: 0, perimeter: null, hacking: 'Y', waterproof: 'Y', hvac: 'N' },
    off:    { roomType: 'Home office',  subtype: 'Single-user office',         area: 120, driver: 1, perimeter: null, hacking: 'N', waterproof: 'N', hvac: 'Y' },
    bal:    { roomType: 'Balcony',      subtype: 'Standard balcony',           area: 80,  driver: 0, perimeter: null, hacking: 'N', waterproof: 'Y', hvac: 'N' },
    opn:    { roomType: 'Open-plan',    subtype: 'Living + Dining + Kitchen',  area: 650, driver: 6, perimeter: null, hacking: 'N', waterproof: 'N', hvac: 'Y' },
    util:   { roomType: 'Utility / yard', subtype: 'Laundry / yard',          area: 70,  driver: 0, perimeter: null, hacking: 'N', waterproof: 'Y', hvac: 'N' },
    foyer:  { roomType: 'Foyer',        subtype: 'Foyer',                     area: 60,  driver: 0, perimeter: null, hacking: 'N', waterproof: 'N', hvac: 'N' },
    others: { roomType: 'Other space',  subtype: 'Other',                     area: 100, driver: 0, perimeter: null, hacking: 'Y', waterproof: 'N', hvac: 'Y' },
    /* The quiz and sib-estimator call this room 'oth'; this table only had 'others', so
       buildRoomInstances' `if (!n || !def) return;` silently dropped it and any project
       including an "Others" space was under-quoted by roughly RM7.5k. Aliased rather than
       renamed so either key resolves. */
    oth:    { roomType: 'Other space',  subtype: 'Other',                     area: 100, driver: 0, perimeter: null, hacking: 'Y', waterproof: 'N', hvac: 'Y' }
  };

  function tierIdxFromName(tierName) { return TIER_NAMES.indexOf(tierName) + 1; } /* 1..3, 0 if not found */

  function ageBandFromYears(existingSiteCondition, years) {
    if (existingSiteCondition === 'new_developer') return '0-5';
    var y = Number(years);
    if (!years || isNaN(y) || y <= 0) return 'Unknown old';
    if (y <= 5) return '0-5';
    if (y <= 15) return '6-15';
    if (y <= 25) return '16-25';
    if (y <= 35) return '26-35';
    if (y <= 50) return '36-50';
    return '50+';
  }

  function effectivePerimeter(area, perimeterInput) {
    if (!(area > 0)) return 0;
    if (perimeterInput > 0) return perimeterInput;
    return 4 * Math.sqrt(area);
  }

  function candidateRules(roomType, subtype) {
    var specific = {}, wildcard = {};
    Data.RULES.forEach(function (r) {
      if (r.roomType !== roomType) return;
      if (r.subtype === subtype) specific[r.item] = r;
      else if (r.subtype === '*') wildcard[r.item] = r;
    });
    var items = {};
    Object.keys(wildcard).forEach(function (k) { items[k] = wildcard[k]; });
    Object.keys(specific).forEach(function (k) { items[k] = specific[k]; });
    return Object.keys(items).map(function (k) { return items[k]; });
  }

  var WORK_GROUP_FLAG = {
    'Hacking': 'hacking', 'Flooring / Tiling': 'flooring', 'Painting': 'painting',
    'Plaster ceiling': 'plasterCeiling', 'Electrical / Lighting': 'electrical',
    'Plumbing / Sanitary': 'plumbing', 'Waterproofing': 'waterproof',
    'Carpentry': 'carpentry', 'HVAC / Aircon': 'hvac', 'FF&E': 'ffe',
    'Wet tiling': 'wetTiling'
  };

  /* Base quantity (07_Calc_BOQ column R equivalent) — tier-scaled, but NOT yet band-scoped
     (Low/Expected/High) and not yet v9 zone/bedroom/large-room adjusted. One-to-one with the
     workbook's nested IF/CHOOSE formula, unchanged from v7. */
  function computeQuantity(rule, ctx) {
    var area = ctx.area, tierIdx = ctx.tierIdx;
    if (!ctx.workSelected) return 0;
    if (area < rule.minArea || area > rule.maxArea) return 0;
    if (tierIdx < rule.minTierIdx || tierIdx > rule.maxTierIdx) return 0;
    var tierMult = [rule.essentialMult, rule.premiumMult, rule.signatureMult][tierIdx - 1];
    var qf = rule.qtyFactor;
    var perim = ctx.perimeter, ceil = ctx.ceilingHeight, driver = ctx.driver;
    var wasteWet = rule.wasteClass === 'Wet';

    switch (rule.qtyMethod) {
      case 'Fixed':
        return rule.baseQty * tierMult * qf;
      case 'Area':
        return area * tierMult * qf;
      case 'AreaWaste':
        return area * tierMult * qf * (1 + (wasteWet ? ctx.wetWaste : ctx.dryWaste));
      case 'WallArea':
        return perim * ceil * rule.coverage * ctx.netPaintWall * tierMult * qf;
      case 'WallPlusCeiling':
        return ((perim * ceil * rule.coverage * ctx.netPaintWall) + area) * tierMult * qf;
      case 'Perimeter':
        return perim * tierMult * qf;
      case 'Lux': {
        var perFixtureLumens = area <= 300 ? 900 : (area <= 700 ? 1200 : 1500);
        var raw = ((area / 10.7639) * rule.targetLux / perFixtureLumens * ctx.lightingAllowance) * tierMult * qf;
        return Math.ceil(raw);
      }
      case 'BaseAreaDriver':
        return Math.min(rule.maxQty, baseAreaDriverRaw(rule, area, driver) * tierMult * qf);
      case 'DriverOrArea':
        if (driver > 0) return driver;
        return Math.min(rule.maxQty, baseAreaDriverRaw(rule, area, driver) * tierMult * qf);
      case 'BathWallTile':
        return perim * rule.tiledHeight * rule.coverage * tierMult * qf * (1 + (wasteWet ? ctx.wetWaste : 0));
      case 'ThresholdFixed':
        return rule.baseQty * tierMult * qf;
      default:
        return 0;
    }
  }
  function baseAreaDriverRaw(rule, area, driver) {
    var stepAdd = area > rule.areaStart ? Math.ceil((area - rule.areaStart) / rule.areaStep) * rule.areaInc : 0;
    var driverAdd = Math.max(driver - rule.driverStart, 0) * rule.driverInc;
    return rule.baseQty + stepAdd + driverAdd;
  }

  /* v7 scope-depth adjustment for Electrical/Plumbing "Point" items — depends on the
     submission's site condition, not just the rule. Unchanged from v7; independent of (and
     stacks with) the v9 band/zone/bedroom/large-room factors below. */
  function applyExistingServiceScope(qty, rule, room, project) {
    if (!(qty > 0)) return qty;
    var esc = rule.existingServiceClass;
    if (esc === 'Electrical Point') {
      if (room.roomType === 'Bathroom') {
        return qty * Data.BATHROOM_SCOPE_FACTORS['Standard renovation'].electrical; /* flat 0.75 */
      }
      if (project.newDeveloper) {
        return Math.max(qty - (rule.newDevBaselineQty || 0), 0);
      }
      return qty * Data.ELECTRICAL_EXTENT_FACTORS['Auto'][1]; /* resale: 0.6 */
    }
    if (esc === 'Plumbing Point') {
      return qty * Data.PLUMBING_EXTENT_FACTORS['Auto'][project.newDeveloper ? 0 : 1]; /* 0 new-dev, 0.5 resale */
    }
    return qty;
  }

  /* v9 whole-home size-zone lookup, by total built-up area. */
  function sizeZoneFor(sqft) {
    for (var i = 0; i < Data.SIZE_ZONES.length; i++) {
      var z = Data.SIZE_ZONES[i];
      if (sqft >= z.minArea && (z.maxArea == null || sqft <= z.maxArea)) return z;
    }
    return Data.SIZE_ZONES[0];
  }
  /* v9 bedroom-aware shared-storage factor, by active bedroom count. */
  function bedroomSharedFactorFor(bedroomCount) {
    for (var i = 0; i < Data.BEDROOM_SHARED_FACTORS.length; i++) {
      var b = Data.BEDROOM_SHARED_FACTORS[i];
      if (bedroomCount >= b.minBedrooms && (b.maxBedrooms == null || bedroomCount <= b.maxBedrooms)) return b.factor;
    }
    return 1;
  }
  /* v9 large-format loose-FF&E size factor, by the ITEM's OWN ROOM area (not whole-home) and tier. */
  function largeRoomScaleFor(roomArea, tierName) {
    for (var i = 0; i < Data.LARGE_ROOM_SCALE.length; i++) {
      var b = Data.LARGE_ROOM_SCALE[i];
      if (roomArea >= b.minArea && (b.maxArea == null || roomArea <= b.maxArea)) return b[tierName] || 1;
    }
    return 1;
  }

  /* v9 band-scope quantity factor: which fraction of `baseQty` (already tier-scaled) applies
     in this band, given the rule's scopeClass. See file header point 1. */
  function scopeBandFactor(rule, band, project) {
    if (rule.scopeClass === 'Core') return 1;
    if (rule.scopeClass === 'Typical') return band === 'low' ? project.lowTypicalScopeWeight : 1;
    /* Optional */
    if (!rule.v9HighEligible) return 0;
    return band === 'high' ? project.zoneHighEligibleWeight : 0;
  }

  /* v9 zone/bedroom quantity factor for this rule (independent of band — applies identically
     to whichever bands the rule appears in at all). See file header points 2-3. */
  function zoneQuantityFactor(rule, project) {
    if (rule.v9ZoneClass === 'Private built-in') return project.zonePrivateFactor;
    if (rule.v9ZoneClass === 'Shared cabinetry') {
      var f = project.zoneSharedFactor;
      if (rule.v9BedroomLinked) f *= project.bedroomSharedFactor;
      return f;
    }
    return 1;
  }

  /* Full v9 quantity for one rule/room/band: base tier-scaled quantity, then existing-service
     scope-depth, then v9 band-scope weight, then v9 zone/bedroom factor, then (for large-
     format loose FF&E) the room-area size factor. Order among the multiplicative factors
     doesn't change the result; this order matches the file header's numbered list. */
  function computeBandQuantity(rule, rateEntry, room, project, band) {
    var base = computeQuantity(rule, {
      area: room.area, perimeter: effectivePerimeter(room.area, room.perimeter),
      ceilingHeight: room.ceilingHeight || project.defaultCeilingHeight, driver: room.driver,
      tierIdx: room.tierIdx, workSelected: room.flags[workFlagFor(rule)] === 'Y',
      dryWaste: project.dryFloorWastage, wetWaste: project.wetTileWastage,
      netPaintWall: project.netPaintWallFactor, lightingAllowance: project.lightingDesignAllowance
    });
    if (!(base > 0)) return 0;
    base = applyExistingServiceScope(base, rule, room, project);
    if (!(base > 0)) return 0;
    var bandFactor = scopeBandFactor(rule, band, project);
    if (!(bandFactor > 0)) return 0;
    var qty = base * bandFactor * zoneQuantityFactor(rule, project);
    if (rateEntry && rateEntry.largeRoomScale) qty *= largeRoomScaleFor(room.area, room.tierName);
    return qty;
  }

  function workFlagFor(rule) {
    if (rule.workGroup === 'Carpentry') {
      if (rule.roomType === 'Kitchen' || rule.item.indexOf('Kitchen') === 0) return '__carpentryKitchen';
      if (rule.roomType === 'Bedroom' && rule.item === 'Wardrobe') return '__carpentryWardrobe';
      return 'carpentry';
    }
    return WORK_GROUP_FLAG[rule.workGroup] || '__none';
  }

  /* v9 band price for one rate item at one tier: handles the Signature brand-mix blend, the
     Premium-Low brand-mix blend, tier-specific low/high factors (FF&E only — every other
     trade falls back to the item's single generic lowFactor/highFactor, exactly as v7). */
  function resolveBandRate(rateEntry, tierName, band) {
    if (tierName === 'Signature' && rateEntry.signatureRate == null) {
      var mix = Data.SIGNATURE_BRAND_MIX[band];
      return rateEntry.premiumRate * mix.premiumShare + rateEntry.luxuryRate * mix.luxuryShare;
    }
    var baseRate = tierName === 'Essential' ? rateEntry.essentialRate
      : tierName === 'Premium' ? rateEntry.premiumRate : rateEntry.signatureRate;
    if (band === 'expected') return baseRate;
    if (tierName === 'Premium' && band === 'low' && rateEntry.ffeBrandMix) {
      return rateEntry.essentialRate * Data.PROJECT_DEFAULTS.premiumLowEssentialShare
        + rateEntry.premiumRate * Data.PROJECT_DEFAULTS.premiumLowPremiumShare;
    }
    var lo, hi;
    if (tierName === 'Essential' && rateEntry.essentialLowFactor != null) {
      lo = rateEntry.essentialLowFactor; hi = rateEntry.essentialHighFactor;
    } else if (tierName === 'Premium' && rateEntry.premiumLowFactor != null) {
      lo = rateEntry.premiumLowFactor; hi = rateEntry.premiumHighFactor;
    } else if (tierName === 'Signature' && rateEntry.signatureLowFactor != null) {
      lo = rateEntry.signatureLowFactor; hi = rateEntry.signatureHighFactor;
    } else {
      lo = rateEntry.lowFactor; hi = rateEntry.highFactor;
    }
    return baseRate * (band === 'low' ? lo : hi);
  }

  /* One BOQ line for one rule applied to one room instance — computes all three bands. */
  function computeLine(rule, room, project) {
    var rateEntry = Data.RATES[rule.item];
    if (!rateEntry) return null; /* should not happen — model is fully cross-checked */

    var out = { roomId: room.id, roomType: room.roomType, item: rule.item, workGroup: rule.workGroup,
      trade: rateEntry.trade, unit: rateEntry.unit, quantity: 0, rate: 0, low: 0, expected: 0, high: 0 };
    var anyBand = false;
    var byBandQty = {}; /* for the display quantity/rate fallback below — a rule can be
                            High-only (Optional, v9HighEligible) with zero Low/Expected qty,
                            so "expected" isn't always where the representative qty/rate live. */
    var byBandRate = {};

    BANDS.forEach(function (band) {
      var qty = computeBandQuantity(rule, rateEntry, room, project, band);
      if (!(qty > 0)) return;
      anyBand = true;
      var rate = resolveBandRate(rateEntry, room.tierName, band);
      byBandQty[band] = qty; byBandRate[band] = rate;
      var ageFactor = rateEntry.ageSensitive ? (Data.AGE_FACTORS[project.ageBand][rateEntry.trade] || 1) : 1;
      var conditionFactor = rateEntry.ageSensitive ? project.conditionFactor : 1;
      var stateFactor = rateEntry.ageSensitive ? project.stateFactor : 1; /* v9: always 1, same as v7 */
      var layoutFactor = rateEntry.layoutSensitive ? (Data.LAYOUT_FACTORS[room.layoutChange] || 1) : 1;
      var rentalFactor = project.rental ? ((Data.RENTAL_FACTORS[rateEntry.rentalClass] || [1, 1, 1])[room.tierIdx - 1]) : 1;
      var scenarioFactor = band === 'low' ? project.lowScenarioFactor : band === 'high' ? project.highScenarioFactor : 1;
      out[band] = qty * rate * ageFactor * conditionFactor * stateFactor * layoutFactor * project.siteFactor * rentalFactor * scenarioFactor;
    });

    if (!anyBand) return null;
    /* Display quantity/rate: prefer Expected (the normal case), then High, then Low — a
       High-only Optional line has no Expected/Low quantity at all, so without this fallback
       it would silently report "0 units" despite pricing a real amount in High. */
    var displayBand = byBandQty.expected != null ? 'expected' : (byBandQty.high != null ? 'high' : 'low');
    out.quantity = byBandQty[displayBand] || 0;
    out.rate = byBandRate[displayBand] || 0;
    return out;
  }

  function computeRoomLines(room, project) {
    var rules = candidateRules(room.roomType, room.subtype);
    var lines = [];
    rules.forEach(function (rule) {
      var line = computeLine(rule, room, project);
      if (line) lines.push(line);
    });
    return lines;
  }

  /* ---- project-level factor resolution (01_Project) ---- */
  function buildProject(input) {
    var d = Data.PROJECT_DEFAULTS;
    var ageBand = ageBandFromYears(input.existingSiteCondition, input.propertyAgeYears);
    var siteFactor = Data.SITE_FACTORS[input.propertyType] || 1;
    var newDeveloper = input.existingSiteCondition === 'new_developer';
    var stateFactor = newDeveloper
      ? Data.STATE_FACTORS['New developer unit'] : Data.STATE_FACTORS['Resale / older property'];
    var conditionFactor = newDeveloper
      ? 1 : (Data.CONDITION_FACTORS[input.propertyPhysicalCondition || 'Average'] || 1);
    var recommendedContingency = Data.CONTINGENCY_BY_AGEBAND[ageBand];
    var effectiveContingency = input.manualContingencyOverride > 0 ? input.manualContingencyOverride : 0;

    var sqft = Math.max(0, Number(input.sqft) || 0);
    var zone = sizeZoneFor(sqft);
    var bedroomCount = Math.max(0, Number(input.bedroomCount) || 0);

    return {
      ageBand: ageBand, siteFactor: siteFactor, stateFactor: stateFactor, conditionFactor: conditionFactor,
      newDeveloper: newDeveloper, rental: input.projectPurpose === 'rental',
      recommendedContingency: recommendedContingency, effectiveContingency: effectiveContingency,
      lowScenarioFactor: d.lowScenarioFactor, highScenarioFactor: d.highScenarioFactor,
      dryFloorWastage: d.dryFloorWastage, wetTileWastage: d.wetTileWastage,
      netPaintWallFactor: d.netPaintWallFactor, lightingDesignAllowance: d.lightingDesignAllowance,
      prelimRate: d.prelimRate, defaultCeilingHeight: d.defaultCeilingHeight,
      lowTypicalScopeWeight: d.lowTypicalScopeWeight,
      sizeZone: zone.zone, zonePrivateFactor: zone.privateFactor, zoneSharedFactor: zone.sharedFactor,
      zoneHighEligibleWeight: zone.highEligibleWeight,
      bedroomCount: bedroomCount, bedroomSharedFactor: bedroomSharedFactorFor(bedroomCount)
    };
  }

  /* ---- room-instance builder: UI counts -> priced room instances ---- */
  function buildRoomInstances(roomCounts, tierName, works, layoutChange) {
    var tierIdx = tierIdxFromName(tierName);
    var flags = {
      flooring: works.flooring ? 'Y' : 'N',
      painting: works.paintingCeiling ? 'Y' : 'N',
      plasterCeiling: works.paintingCeiling ? 'Y' : 'N',
      electrical: works.electrical ? 'Y' : 'N',
      plumbing: works.plumbing ? 'Y' : 'N',
      carpentry: (works.kitchenCarpentry || works.builtInWardrobe) ? 'Y' : 'N',
      __carpentryKitchen: works.kitchenCarpentry ? 'Y' : 'N',
      __carpentryWardrobe: works.builtInWardrobe ? 'Y' : 'N',
      ffe: works.ffe ? 'Y' : 'N',
      wetTiling: works.wetTiling ? 'Y' : 'N'
    };
    var rooms = [];
    Object.keys(roomCounts).forEach(function (key) {
      var n = roomCounts[key] || 0;
      var def = ROOM_DEFAULTS[key];
      if (!n || !def) return;
      for (var i = 0; i < n; i++) {
        rooms.push({
          id: key + '-' + (i + 1), roomType: def.roomType, subtype: def.subtype,
          area: def.area, perimeter: def.perimeter, ceilingHeight: null, driver: def.driver,
          tierName: tierName, tierIdx: tierIdx, layoutChange: layoutChange || 'Keep layout',
          flags: {
            hacking: def.hacking, waterproof: def.waterproof, hvac: def.hvac,
            flooring: flags.flooring, painting: flags.painting, plasterCeiling: flags.plasterCeiling,
            electrical: flags.electrical, plumbing: flags.plumbing, carpentry: flags.carpentry,
            __carpentryKitchen: flags.__carpentryKitchen, __carpentryWardrobe: flags.__carpentryWardrobe,
            ffe: flags.ffe, wetTiling: flags.wetTiling
          }
        });
      }
    });
    return rooms;
  }

  /* ---- kitchen reconciliation -------------------------------------------------------------

     The rate rules match on roomType and are generated from the V9 workbook, which has no
     "wet kitchen" type and must not be hand-edited. Three corrections therefore have to happen
     after the lines are built, all of them about the same thing: a home has ONE set of kitchen
     fixtures, however many boxes the visitor ticks.

     A. Open-plan is subtype "Living + Dining + Kitchen", so it already carries a full cooking
        package. Ticking Dry kitchen as well billed a SECOND complete kitchen — cabinets,
        countertop, hood, appliances, sink, backsplash — roughly RM42.6k of duplication at
        premium tier. The open-plan kitchen stands; the standalone pick keeps only its shell
        (floor, ceiling, paint, sockets). Only the first Dry kitchen instance is reconciled:
        someone who genuinely ticks two kitchens still gets the second one priced.

     B. The Wet kitchen runs on the Utility / yard rules, which give it a washing machine and a
        drying rack but no cooking hardware at all. In a Malaysian home that is backwards — the
        wet kitchen is where the wok actually runs. It now takes a share of the kitchen it sits
        beside, scaled from that kitchen's own line items so the trade split and the printed BOQ
        stay honest rather than collapsing into one opaque sum.

     C. Laundry belongs in one room. Wet kitchen + Utility/yard billed two washer/dryer sets,
        two drying racks, two utility sinks and two runs of laundry cabinetry. When a Utility /
        yard is also selected it owns the laundry, and the wet kitchen drops it.

     Rates set by the owner on 2026-08-31, deliberately conservative against the market ratio:
       with Open-plan   50% -> kitchen spend 140% of one kitchen
       with Dry kitchen 20% -> kitchen spend 120%
       neither          50% -> the wet kitchen is the only kitchen in the home            */
  var COOKING_ITEM = /^(kitchen (base|wall|tall) cabinet|kitchen countertop|kitchen backsplash|kitchen sink & tap|kitchen appliance set|hood & hob)$/i;
  var LAUNDRY_ITEM = /^(washer \/ dryer set|laundry drying rack|utility sink & tap|laundry \/ utility cabinetry)$/i;
  var WET_RATE_WITH_OPEN_PLAN = 0.50;
  var WET_RATE_WITH_DRY_KITCHEN = 0.20;
  var WET_RATE_SOLE_KITCHEN = 0.50;

  function reconcileKitchens(lines, input, project) {
    var counts = input.roomCounts || {};
    var has = function (k) { return (counts[k] || 0) > 0; };
    if (!has('wetkit') && !(has('opn') && has('kit'))) return lines;
    var out = lines;

    /* A. one cooking package when open-plan and a standalone kitchen are both ticked */
    if (has('opn') && has('kit')) {
      out = out.filter(function (l) {
        return !(l.roomId === 'kit-1' && COOKING_ITEM.test(l.item));
      });
    }

    if (has('wetkit')) {
      /* Reference kitchen, priced on its own so the share never depends on what survived step A.
         Open-plan's kitchen is the reference when there is one; otherwise the standalone kitchen,
         built synthetically when the visitor did not tick it. */
      var srcKey = has('opn') ? 'opn' : 'kit';
      var rate = has('opn') ? WET_RATE_WITH_OPEN_PLAN
        : (has('kit') ? WET_RATE_WITH_DRY_KITCHEN : WET_RATE_SOLE_KITCHEN);
      var refCounts = {}; refCounts[srcKey] = 1;
      var refRoom = buildRoomInstances(refCounts, input.finishTier, input.works, input.layoutChange)[0];
      var cooking = refRoom ? computeRoomLines(refRoom, project).filter(function (l) {
        return COOKING_ITEM.test(l.item);
      }) : [];

      /* C. the utility room owns the laundry when it exists */
      if (has('util')) {
        out = out.filter(function (l) {
          return !(/^wetkit-/.test(l.roomId) && LAUNDRY_ITEM.test(l.item));
        });
      }

      /* B. scale the reference kitchen's own lines into every wet kitchen instance */
      for (var i = 1; i <= counts.wetkit; i++) {
        cooking.forEach(function (l) {
          out.push({
            roomId: 'wetkit-' + i, roomType: ROOM_DEFAULTS.wetkit.roomType,
            item: l.item, workGroup: l.workGroup, trade: l.trade, unit: l.unit,
            quantity: Math.round(l.quantity * rate * 100) / 100, rate: l.rate,
            low: l.low * rate, expected: l.expected * rate, high: l.high * rate
          });
        });
      }
    }
    return out;
  }

  /* ---- full project estimate: rooms -> direct BOQ (3 bands) -> preliminaries -> contingency ---- */
  function estimateProject(input) {
    var bedroomCount = (input.roomCounts && input.roomCounts.bed) || 0;
    var project = buildProject(Object.assign({}, input, { bedroomCount: bedroomCount }));
    var rooms = buildRoomInstances(input.roomCounts, input.finishTier, input.works, input.layoutChange);
    var allLines = [];
    rooms.forEach(function (room) { allLines = allLines.concat(computeRoomLines(room, project)); });
    allLines = reconcileKitchens(allLines, input, project);

    var directLow = 0, directExpected = 0, directHigh = 0;
    var byTrade = {}, byRoom = {};
    allLines.forEach(function (l) {
      directLow += l.low; directExpected += l.expected; directHigh += l.high;
      byTrade[l.trade] = byTrade[l.trade] || { low: 0, expected: 0, high: 0 };
      byTrade[l.trade].low += l.low; byTrade[l.trade].expected += l.expected; byTrade[l.trade].high += l.high;
      byRoom[l.roomId] = byRoom[l.roomId] || { roomType: l.roomType, low: 0, expected: 0, high: 0 };
      byRoom[l.roomId].low += l.low; byRoom[l.roomId].expected += l.expected; byRoom[l.roomId].high += l.high;
    });

    function totalFrom(direct) {
      var prelim = direct * project.prelimRate;
      var subtotal = direct + prelim;
      var contingency = subtotal * project.effectiveContingency;
      return subtotal + contingency;
    }

    return {
      ageBand: project.ageBand, effectiveContingency: project.effectiveContingency,
      recommendedContingency: project.recommendedContingency,
      sizeZone: project.sizeZone, bedroomCount: project.bedroomCount,
      directBoq: { low: directLow, expected: directExpected, high: directHigh },
      total: { low: totalFrom(directLow), expected: totalFrom(directExpected), high: totalFrom(directHigh) },
      byTrade: byTrade, byRoom: byRoom, lines: allLines, roomCount: rooms.length
    };
  }

  return {
    ROOM_DEFAULTS: ROOM_DEFAULTS,
    ageBandFromYears: ageBandFromYears,
    tierIdxFromName: tierIdxFromName,
    effectivePerimeter: effectivePerimeter,
    candidateRules: candidateRules,
    computeQuantity: computeQuantity,
    computeBandQuantity: computeBandQuantity,
    resolveBandRate: resolveBandRate,
    sizeZoneFor: sizeZoneFor,
    bedroomSharedFactorFor: bedroomSharedFactorFor,
    largeRoomScaleFor: largeRoomScaleFor,
    computeLine: computeLine,
    computeRoomLines: computeRoomLines,
    buildProject: buildProject,
    buildRoomInstances: buildRoomInstances,
    estimateProject: estimateProject
  };
}));
